import { t } from "i18next";
import ceil from "lodash/ceil";
import { useMemo } from "react";
import { Trans } from "react-i18next";

import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { getNestingLabel } from "#modules/quoteRequestLines/helpers/getNestingLabel";
import { MaterialProperty } from "#modules/quoteRequestLines/views/DetailsPanel/Material/MaterialProperty";
import { Icon } from "#shared/icons/Icon";
import { Alert } from "#shared/view/components/Alert";
import { Flex } from "#shared/view/components/Flex";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { formatMaterialDimensions, getMaterialWeight, Layout, MaterialLayout } from "shared";

type MaterialLayoutsProps = {
  layout: Layout;
  materialLayout: MaterialLayout;
  partWithOverthicknessDimensions: PartDto | undefined;
};

export const MaterialLayouts = ({
  layout,
  materialLayout,
  partWithOverthicknessDimensions,
}: MaterialLayoutsProps) => {
  const nestingLabel = getNestingLabel(partWithOverthicknessDimensions?.type);

  const maxPartsPerLayout = materialLayout.maxPartsPerLayout;

  const nestedVolume = useMemo(
    () => (materialLayout.volume ?? 0) / (maxPartsPerLayout ?? 1),
    [materialLayout.volume, maxPartsPerLayout],
  );

  const weight = useMemo(
    () => ceil(getMaterialWeight(nestedVolume, materialLayout.density), 3),
    [nestedVolume, materialLayout.density],
  );

  if (!maxPartsPerLayout || maxPartsPerLayout <= 0) {
    return (
      <Alert p="xs" maw="550px" icon={<Icon name="infoCircle" />}>
        <Text fz={12}>{t("quoteRequestLine.nestingImpossibleWarning")}</Text>
        <ul style={{ margin: 0, paddingLeft: 12 }}>
          <li>
            <Text fz={12}>
              <Trans
                i18nKey="quoteRequestLine.nestingImpossibleWarningPartDimensionsDetails"
                values={{
                  dimensions: partWithOverthicknessDimensions
                    ? formatMaterialDimensions(
                        partWithOverthicknessDimensions.type,
                        partWithOverthicknessDimensions,
                      )
                    : "-",
                }}
                components={{ bold: <strong /> }}
              />
            </Text>
          </li>
          <li>
            <Text fz={12}>
              <Trans
                i18nKey="quoteRequestLine.nestingImpossibleWarningMaterialLayoutDimensionsDetails"
                values={{
                  dimensions: formatMaterialDimensions(materialLayout.type, materialLayout),
                }}
                components={{ bold: <strong /> }}
              />
            </Text>
          </li>
          <li>
            <Text fz={12}>
              <Trans
                i18nKey="quoteRequestLine.nestingImpossibleWarningCutWidthDetails"
                values={{ cutWidth: layout.cutWidth }}
                components={{ bold: <strong /> }}
              />
            </Text>
          </li>
          <li>
            <Text fz={12}>
              <Trans
                i18nKey="quoteRequestLine.nestingImpossibleWarningSkeletonWidthDetails"
                values={{ skeletonWidth: layout.skeletonWidth }}
                components={{ bold: <strong /> }}
              />
            </Text>
          </li>
        </ul>
      </Alert>
    );
  }

  return (
    <Stack gap="xxxs" data-testid="material-nesting-summary">
      <Flex gap="0">
        <MaterialProperty
          icon="layout"
          count={maxPartsPerLayout}
          label={t("quoteRequestLine.part")}
        />
        {/** We force an irregular space before the 'maximum' word because
         * Mantine <Text> component trim regular space in text. So do not remove the
         * space (\u00a0) before the word.
         */}
        <Text>{`${t("quoteRequestLine.maxPartsPerLayout")} ${nestingLabel}`}</Text>
      </Flex>

      <MaterialProperty
        icon="layer"
        count={materialLayout.quantity}
        label={`${nestingLabel} ${t("quoteRequestLine.rawMaterialRequired")}`}
      />
      <MaterialProperty
        icon="weight"
        count={weight}
        unit="kg"
        label={t("quoteRequestLine.nestedMaterialWeight")}
        pluralize={false}
      />
    </Stack>
  );
};
